import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Image, Input, List, ListItem, Portal, Spinner, Text } from '@chakra-ui/react';
import { BrandSearchResultDto } from '@foodfluence/shared';
import { brandApi } from '../../../services/api/brand';
import { bountyV2Colors } from '../theme';

type Props = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  inputStyle?: Record<string, unknown>;
};

type MenuPosition = { top: number; left: number; width: number };

const BountyBrandSearchInput: React.FC<Props> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Start typing to search brands',
  inputStyle = {},
}) => {
  const [suggestions, setSuggestions] = useState<BrandSearchResultDto[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);

  const syncMenuPosition = () => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!showSuggestions || suggestions.length === 0) return;
    syncMenuPosition();
    const reposition = () => syncMenuPosition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [showSuggestions, suggestions]);

  const fetchSuggestions = useCallback(async (input: string) => {
    const requestId = ++requestIdRef.current;
    if (input.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSearchPending(false);
      return;
    }
    setIsSearchPending(true);
    try {
      const results = await brandApi.searchBrands(input.trim());
      if (requestId !== requestIdRef.current) return;
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      if (requestId === requestIdRef.current) setIsSearchPending(false);
    }
  }, []);

  const debouncedFetch = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    return (input: string) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => void fetchSuggestions(input), 300);
    };
  }, [fetchSuggestions]);

  useEffect(() => () => { requestIdRef.current += 1; }, []);

  const handleChange = (next: string) => {
    onChange(next);
    debouncedFetch(next);
  };

  const handleSelect = (brand: BrandSearchResultDto) => {
    onChange(brand.name);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <Box position="relative" flex={1} minW={0} ref={containerRef}>
      <Flex align="center" position="relative">
        <Input
          variant="unstyled"
          flex={1}
          minW={0}
          fontSize="16px"
          color={bountyV2Colors.ink}
          _placeholder={{ color: bountyV2Colors.placeholder }}
          placeholder={placeholder}
          autoComplete="off"
          value={value}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => {
            syncMenuPosition();
            if (value.trim().length >= 2 && suggestions.length > 0) setShowSuggestions(true);
          }}
          onBlur={() => {
            setTimeout(() => setShowSuggestions(false), 200);
            onBlur?.();
          }}
          pr={isSearchPending ? '28px' : undefined}
          {...inputStyle}
        />
        {isSearchPending && (
          <Box position="absolute" right={0} pointerEvents="none">
            <Spinner size="xs" color={bountyV2Colors.placeholder} />
          </Box>
        )}
      </Flex>

      {showSuggestions && suggestions.length > 0 && menuPos && (
        <Portal>
          <List
            position="fixed"
            top={`${menuPos.top}px`}
            left={`${menuPos.left}px`}
            width={`${menuPos.width}px`}
            bg="white"
            boxShadow="0 4px 20px rgba(15,23,42,0.12)"
            borderRadius="12px"
            maxH="220px"
            overflowY="auto"
            zIndex={2000}
            border="1px solid"
            borderColor={bountyV2Colors.fieldBorder}
            py="4px"
          >
            {suggestions.map(suggestion => (
              <ListItem
                key={suggestion.name + suggestion.icon}
                py="10px"
                px="12px"
                cursor="pointer"
                _hover={{ bg: '#F2F2F0' }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(suggestion)}
                display="flex"
                alignItems="center"
                gap="10px"
              >
                {suggestion.icon && (
                  <Image
                    src={suggestion.icon}
                    alt=""
                    boxSize="28px"
                    borderRadius="8px"
                    objectFit="contain"
                    flexShrink={0}
                  />
                )}
                <Text fontWeight={600} fontSize="14px" color={bountyV2Colors.ink} noOfLines={2}>
                  {suggestion.name}
                </Text>
              </ListItem>
            ))}
          </List>
        </Portal>
      )}
    </Box>
  );
};

export default BountyBrandSearchInput;
